import { FC } from 'react';
import { useEffectOnce } from 'usehooks-ts';
import { Trans, useTranslation } from 'react-i18next';
import { ModalComponentBaseProps } from '@pcmp/react-providers';
import { Sheets, ModalHeader, ModalBody } from '@ingka/modal';
import { useTrackContent } from '@/lib/hooks/tracking';
import { useModalGoToProfile } from '@/lib/hooks/modal';

type PrivacyPolicySiderProps = ModalComponentBaseProps;

const usePrivacyPolicyTracking = () => {
  const [trackContent] = useTrackContent();

  useEffectOnce(() => {
    trackContent({ content: 'privacy-policy', type: 'sider' });
  });
};

export const PrivacyPolicySider: FC<PrivacyPolicySiderProps> = ({
  handleDismiss,
  ...rest
}) => {
  const { t } = useTranslation();
  const goBack = useModalGoToProfile();

  usePrivacyPolicyTracking();

  return (
    <Sheets
      {...rest}
      alignment="right"
      aria-label={t('page.contact.shortTitle')}
      footer={null}
      header={
        <ModalHeader ariaCloseTxt={t('label.close')} backBtnClick={goBack} />
      }
      size="medium"
    >
      <ModalBody data-testid="privacyPolicySider">
        <h1 className="text-2xl font-bold leading-loose">
          <Trans i18nKey="page.privacyPolicy.title" />
        </h1>
        <div className="mt-[10px] text-sm font-normal leading-relaxed">
          <p>
            <Trans i18nKey="page.privacyPolicy.description" />
          </p>

          <br />
          <p>
            <Trans i18nKey="page.privacyPolicy.dataProcessed.intro" />
          </p>
          <p>
            <Trans i18nKey="page.privacyPolicy.dataProcessed.coworkers" />
          </p>
          <p>
            <Trans i18nKey="page.privacyPolicy.dataProcessed.users" />
          </p>

          <br />
          <p>
            <Trans i18nKey="page.privacyPolicy.purpose.intro" />
          </p>
          <p>
            <Trans i18nKey="page.privacyPolicy.purpose.management" />
          </p>
          <p>
            <Trans i18nKey="page.privacyPolicy.purpose.scan" />
          </p>

          <br />
          <p>
            <Trans i18nKey="page.privacyPolicy.legalGround.intro" />
          </p>
          <p>
            <Trans i18nKey="page.privacyPolicy.legalGround.description" />
          </p>
          <ul>
            <li>
              <Trans i18nKey="page.privacyPolicy.legalGround.list.item1" />
            </li>
            <li>
              <Trans i18nKey="page.privacyPolicy.legalGround.list.item2" />
            </li>
            <li>
              <Trans i18nKey="page.privacyPolicy.legalGround.list.item3" />
            </li>
            <li>
              <Trans i18nKey="page.privacyPolicy.legalGround.list.item4" />
            </li>
          </ul>

          <br />
          <p>
            <Trans i18nKey="page.privacyPolicy.duration" />
          </p>
          <p>
            <Trans i18nKey="page.privacyPolicy.controller" />
          </p>

          <br />
          <h2 className="text-xl font-bold leading-loose mt-3">
            <Trans i18nKey="page.privacyPolicy.yourRights.title" />
          </h2>
          <p>
            <Trans
              i18nKey="page.privacyPolicy.yourRights.description"
              components={{
                link: (
                  <a
                    href="https://inside.ingka.com/supportservices/Dataprivacy/Pages/Individual-rights.aspx"
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    https://inside.ingka.com/supportservices/Dataprivacy/Pages/Individual-rights.aspx
                  </a>
                ),
              }}
            />
          </p>
        </div>
      </ModalBody>
    </Sheets>
  );
};
